import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronRight, Layers, ChevronUp, ChevronDown, GripVertical, Save, Trash2 } from 'lucide-react';
import { sectorsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AdminSectors = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [sectorToDelete, setSectorToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await sectorsApi.getAll();
        setSectors(response.data);
      } catch (error) {
        console.error('Error fetching sectors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSectors();
  }, []);

  const moveSector = (index, direction) => {
    const newSectors = [...sectors];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSectors.length) return;
    
    [newSectors[index], newSectors[targetIndex]] = [newSectors[targetIndex], newSectors[index]];
    
    newSectors.forEach((sector, idx) => {
      sector.order = idx + 1;
    });
    
    setSectors(newSectors);
    setHasChanges(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const orderUpdates = sectors.map((sector, idx) => ({
        id: sector.id,
        order: idx + 1
      }));
      await sectorsApi.updateOrder(orderUpdates);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving order:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSector = async () => {
    if (!sectorToDelete) return;
    setDeleting(true);
    try {
      await sectorsApi.delete(sectorToDelete.id);
      setSectors(sectors.filter(s => s.id !== sectorToDelete.id));
      setSectorToDelete(null);
    } catch (error) {
      console.error('Error deleting sector:', error);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="admin-sectors-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Sectors</h1>
          <p className="text-gray-500 mt-1">Manage sector landing pages</p>
        </div>
        {hasChanges && (
          <Button 
            onClick={saveOrder} 
            disabled={saving}
            data-testid="save-sector-order-btn"
            variant="outline"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        )}
      </div>

      {hasChanges && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          You have unsaved changes to the sector order. Click "Save Order" to apply.
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm divide-y">
        {sectors.map((sector, index) => (
          <div
            key={sector.id}
            className="flex items-center hover:bg-gray-50 transition-colors group"
            data-testid={`sector-item-${sector.id}`}
          >
            {/* Reorder Controls */}
            <div className="flex flex-col items-center justify-center px-3 py-4 bg-gray-50 border-r">
              <button
                onClick={() => moveSector(index, 'up')}
                disabled={index === 0}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`move-sector-up-${sector.id}`}
                aria-label={`Move ${sector.name} up`}
              >
                <ChevronUp className="h-5 w-5 text-gray-600" />
              </button>
              <GripVertical className="h-5 w-5 text-gray-400 my-1" />
              <button
                onClick={() => moveSector(index, 'down')}
                disabled={index === sectors.length - 1}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`move-sector-down-${sector.id}`}
                aria-label={`Move ${sector.name} down`}
              >
                <ChevronDown className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Sector Content */}
            <Link
              to={`/admin/sectors/${sector.id}`}
              className="flex-1 flex items-center p-4"
            >
              <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden mr-4">
                {sector.heroImages?.[0] ? (
                  <img
                    src={sector.heroImages[0]}
                    alt={sector.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Layers className="h-6 w-6 text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                    #{index + 1}
                  </span>
                  <h3 className="font-medium text-gray-900 group-hover:text-gray-700">
                    {sector.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 truncate mt-1">
                  {sector.title || 'No title set'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">
                    {sector.selectedBrands?.length || 0} brands selected
                  </span>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-gray-400">
                    {sector.heroImages?.filter(Boolean).length || 0}/3 images
                  </span>
                </div>
              </div>

              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
            </Link>

            {/* Delete Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSectorToDelete(sector);
              }}
              className="p-3 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Delete sector"
              data-testid={`delete-sector-${sector.id}`}
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>

      <p className="mt-4 text-sm text-gray-500">
        Use the arrow buttons to reorder sectors in the navigation dropdown. Click on a sector to edit its content.
      </p>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!sectorToDelete} onOpenChange={() => setSectorToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sector</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{sectorToDelete?.name}</strong>? 
              This will permanently remove the sector and its landing page. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSector}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                'Delete Sector'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSectors;
